import React, { useState, useEffect, useMemo } from "react";
import { useApi } from "../hooks/useApi";
import Card from "../components/Card";
import {
    ComposedChart, Area, Line, XAxis, YAxis, CartesianGrid, Tooltip,
    ResponsiveContainer, ReferenceLine, ReferenceArea, Legend, LabelList
} from "recharts";

const TIER_COLORS = { 1: "#ef4444", 2: "#f59e0b", 3: "#22c55e" };
const TIER_LABELS = { 1: "Critical", 2: "Important", 3: "Standard" };
const RISK_SCORE_MODEL_MAX = 97; // MonetaryImpactEngine.MaxRiskScore — forecast plateaus here, never 100.

// Labels the 45/90/180-day forecast points directly above the dashed trajectory line so the
// irregular calendar spacing on the X-axis maps back to the horizon cards.
const HorizonLabel = ({ x, y, value }) => {
    if (value == null) return null;
    return (
        <text x={x} y={y - 10} fill="#8b5cf6" fontSize={10} fontWeight="600" textAnchor="middle">
            {value} Days
        </text>
    );
};

const formatDate = (dateStr) => {
    const d = new Date(dateStr);
    return `${d.getUTCMonth() + 1}/${d.getUTCDate()}`;
};

const addDaysToDate = (dateStr, n) => {
    const [y, m, d] = dateStr.split("-").map(Number);
    const dt = new Date(y, m - 1, d);
    dt.setDate(dt.getDate() + n);
    return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
};

const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const data = payload[0]?.payload;
    if (!data) return null;

    if (data.isForecast) {
        return (
            <div className="bg-white border border-purple-200 rounded-lg shadow-lg p-3 text-sm">
                <div className="font-semibold mb-1 text-purple-700">
                    {new Date(data.snapshotDate).toLocaleDateString(undefined, { timeZone: "UTC" })} — Forecast
                </div>
                <div className="flex justify-between gap-4">
                    <span className="text-gray-500">Projected Score:</span>
                    <span className="font-medium text-purple-700">{data.forecastScore}%</span>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3 text-sm">
            <div className="font-semibold mb-1">
                {new Date(data.snapshotDate).toLocaleDateString(undefined, { timeZone: "UTC" })}
            </div>
            <div className="space-y-1">
                <div className="flex justify-between gap-4">
                    <span className="text-gray-500">Risk Score:</span>
                    <span className="font-medium">{data.riskScore}%</span>
                </div>
                {data.vendorTier && (
                    <div className="flex justify-between gap-4">
                        <span className="text-gray-500">Tier:</span>
                        <span className="font-medium">{TIER_LABELS[data.vendorTier] || data.vendorTier}</span>
                    </div>
                )}
                <div className="border-t border-gray-100 pt-1 mt-1">
                    <div className="flex justify-between gap-4"><span className="text-red-600">Critical:</span><span>{data.criticalCount}</span></div>
                    <div className="flex justify-between gap-4"><span className="text-orange-600">High:</span><span>{data.highCount}</span></div>
                    <div className="flex justify-between gap-4"><span className="text-yellow-600">Medium:</span><span>{data.mediumCount}</span></div>
                    <div className="flex justify-between gap-4"><span className="text-green-600">Low:</span><span>{data.lowCount}</span></div>
                </div>
            </div>
        </div>
    );
};

// forecast prop: VendorDeteriorationForecastDto (passed from VendorDashboard)
const VendorScoreTrend = ({ vendorId, forecast }) => {
    const apiCall = useApi();
    const [snapshots, setSnapshots] = useState([]);
    const [loading, setLoading] = useState(true);
    const [days, setDays] = useState(90);
    const [maxAvailableDays, setMaxAvailableDays] = useState(0);

    useEffect(() => {
        const fetchScoreHistory = async () => {
            setLoading(true);
            try {
                const data = await apiCall("GET", `/TPRMVendor/${vendorId}/score-history?days=${days}`);
                const fetched = Array.isArray(data) ? data : [];
                setSnapshots(fetched);

                if (fetched.length >= 2) {
                    const spanDays = Math.round(
                        (new Date(fetched[fetched.length - 1].snapshotDate) - new Date(fetched[0].snapshotDate)) / 86_400_000
                    );
                    setMaxAvailableDays(prev => Math.max(prev, spanDays));

                    if (spanDays < days) {
                        const RANGES = [30, 60, 90, 180, 365];
                        const best = RANGES.find(d => d <= spanDays) ?? RANGES[0];
                        if (best !== days) { setDays(best); return; }
                    }
                }
            } catch (err) {
                setSnapshots([]);
            } finally {
                setLoading(false);
            }
        };
        fetchScoreHistory();
    }, [vendorId, days, apiCall]);

    const { scoreChange, actualSpanDays, minScore } = useMemo(() => {
        if (snapshots.length < 2) return { scoreChange: null, actualSpanDays: 0, minScore: 0 };
        const first = snapshots[0];
        const last = snapshots[snapshots.length - 1];
        return {
            scoreChange: last.riskScore - first.riskScore,
            actualSpanDays: Math.round((new Date(last.snapshotDate) - new Date(first.snapshotDate)) / 86_400_000),
            minScore: Math.min(...snapshots.map(s => s.riskScore)),
        };
    }, [snapshots]);

    // Merge historical snapshots with forecast projection points
    const allChartData = useMemo(() => {
        if (!snapshots.length) return [];

        const data = snapshots.map(s => ({ ...s, forecastScore: null, isForecast: false }));

        if (forecast?.score90Day != null) {
            // Bridge: last historical point connects to the forecast line
            data[data.length - 1] = {
                ...data[data.length - 1],
                forecastScore: data[data.length - 1].riskScore,
            };

            const lastDate = snapshots[snapshots.length - 1].snapshotDate;

            // Always plot 45d / 90d / 180d so the chart keeps its full visual window.
            // When scores are clamped to 100% the line rises then stays flat — correct.
            if (forecast.score45Day != null) {
                data.push({ snapshotDate: addDaysToDate(lastDate, 45),  forecastScore: forecast.score45Day,  riskScore: null, isForecast: true, horizon: 45 });
            }
            data.push(    { snapshotDate: addDaysToDate(lastDate, 90),  forecastScore: forecast.score90Day,  riskScore: null, isForecast: true, horizon: 90 });
            if (forecast.score180Day != null) {
                data.push({ snapshotDate: addDaysToDate(lastDate, 180), forecastScore: forecast.score180Day, riskScore: null, isForecast: true, horizon: 180 });
            }
        }
        return data;
    }, [snapshots, forecast]);

    const chartMin = snapshots.length > 0
        ? Math.max(0, Math.floor((minScore - 10) / 10) * 10)
        : 0;

    const tierChanges = useMemo(() => {
        const changes = [];
        for (let i = 1; i < snapshots.length; i++) {
            if (snapshots[i].vendorTier !== snapshots[i - 1].vendorTier && snapshots[i].vendorTier)
                changes.push({ date: snapshots[i].snapshotDate, to: snapshots[i].vendorTier });
        }
        return changes;
    }, [snapshots]);

    const forecastBandDates = useMemo(() => {
        if (!snapshots.length || !forecast?.confidenceBandLow) return null;
        const lastDate = snapshots[snapshots.length - 1].snapshotDate;
        return { x1: lastDate, x2: addDaysToDate(lastDate, 90) };
    }, [snapshots, forecast]);

    if (loading) {
        return (
            <Card>
                <div className="flex items-center justify-center py-12 text-gray-400">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mr-3"></div>
                    Loading score history...
                </div>
            </Card>
        );
    }

    if (snapshots.length === 0) {
        return (
            <Card>
                <h3 className="text-lg font-semibold mb-2">Score Trend</h3>
                <div className="text-center py-12 text-gray-500">
                    <p>No score history available yet.</p>
                    <p className="text-sm mt-1">Snapshots are recorded daily. Check back after the next scan cycle.</p>
                </div>
            </Card>
        );
    }

    return (
        <div className="space-y-4">
            <Card>
                <div className="flex justify-between items-center mb-1">
                    <div>
                        <h3 className="text-lg font-semibold">Risk Score Trend</h3>
                        <p className="text-xs text-gray-400 mt-0.5">
                            Anchored to the worst active finding severity, not finding volume — the score can step up or down
                            when severity changes even while the finding count stays flat.
                            {forecast?.score90Day != null && (
                                <span className="text-purple-500 ml-1">
                                    Dashed line shows AI-predicted trajectory
                                    {forecast.score90Day >= RISK_SCORE_MODEL_MAX
                                        ? `, which plateaus at the model maximum of ${RISK_SCORE_MODEL_MAX}%.`
                                        : "."}
                                </span>
                            )}
                        </p>
                    </div>
                    <div className="flex gap-2">
                        {[30, 60, 90, 180, 365].map(d => {
                            const insufficient = maxAvailableDays > 0 && d > maxAvailableDays;
                            return (
                                <button
                                    key={d}
                                    onClick={() => !insufficient && setDays(d)}
                                    disabled={insufficient}
                                    className={`px-3 py-1 text-xs rounded-full transition-colors ${
                                        days === d
                                            ? "bg-blue-600 text-white"
                                            : insufficient
                                            ? "bg-gray-100 text-gray-300 cursor-not-allowed"
                                            : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                                    }`}
                                >
                                    {d}d
                                </button>
                            );
                        })}
                    </div>
                </div>

                {/* Summary metrics */}
                {scoreChange !== null && (
                    <div className="grid grid-cols-3 gap-3 mb-3">
                        <div
                            className="p-3 bg-gray-50 rounded-lg"
                            title="The score is the latest observed daily snapshot. The range is a modeled 90% band from recent day-to-day volatility (±1.645σ over the last 14 days) — it shows how much the score has been moving, not measurement error."
                        >
                            <div className="text-xs text-gray-500 uppercase">Current Score</div>
                            <div className="text-xl font-bold">{snapshots[snapshots.length - 1].riskScore}%</div>
                            {forecast?.currentScoreConfidenceLow != null && (
                                <div className="text-xs text-gray-400 mt-0.5">
                                    Est. range: {forecast.currentScoreConfidenceLow}% – {forecast.currentScoreConfidenceHigh}% <span className="text-gray-300">(90% CI, recent volatility)</span>
                                </div>
                            )}
                        </div>
                        <div className="p-3 bg-gray-50 rounded-lg">
                            <div className="text-xs text-gray-500 uppercase">
                                Change ({actualSpanDays}d{actualSpanDays < days ? ` of ${days}d` : ""})
                            </div>
                            <div className={`text-xl font-bold ${
                                scoreChange > 0 ? "text-red-600" :
                                scoreChange < 0 ? "text-green-600" : "text-gray-600"
                            }`}>
                                {scoreChange > 0 ? "+" : ""}{scoreChange} pts
                            </div>
                        </div>
                        <div className="p-3 bg-gray-50 rounded-lg">
                            <div className="text-xs text-gray-500 uppercase">Findings</div>
                            <div className="text-xl font-bold">{snapshots[snapshots.length - 1].totalFindings}</div>
                        </div>
                    </div>
                )}

                {/* Chart */}
                <ResponsiveContainer width="100%" height={300}>
                    <ComposedChart data={allChartData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                        <defs>
                            <linearGradient id="scoreGradient" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%"  stopColor="#3b82f6" stopOpacity={0.3} />
                                <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                            </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis dataKey="snapshotDate" tickFormatter={formatDate} tick={{ fontSize: 12 }} stroke="#9ca3af" />
                        <YAxis
                            domain={[chartMin, 100]}
                            ticks={Array.from({ length: (100 - chartMin) / 10 + 1 }, (_, i) => chartMin + i * 10)}
                            tick={{ fontSize: 12 }}
                            stroke="#9ca3af"
                            tickFormatter={v => `${v}%`}
                        />
                        <Tooltip content={<CustomTooltip />} />

                        {/* Current score confidence band shading */}
                        {forecast?.currentScoreConfidenceLow != null && snapshots.length >= 2 && (
                            <ReferenceArea
                                x1={snapshots[0].snapshotDate}
                                x2={snapshots[snapshots.length - 1].snapshotDate}
                                y1={forecast.currentScoreConfidenceLow}
                                y2={forecast.currentScoreConfidenceHigh}
                                fill="#3b82f6"
                                fillOpacity={0.05}
                                stroke="none"
                            />
                        )}

                        {/* Forecast confidence band shading */}
                        {forecastBandDates && forecast?.confidenceBandLow != null && (
                            <ReferenceArea
                                x1={forecastBandDates.x1}
                                x2={forecastBandDates.x2}
                                y1={forecast.confidenceBandLow}
                                y2={forecast.confidenceBandHigh}
                                fill="#8b5cf6"
                                fillOpacity={0.08}
                                stroke="none"
                            />
                        )}

                        {/* Tier change markers */}
                        {tierChanges.map(tc => (
                            <ReferenceLine
                                key={tc.date}
                                x={tc.date}
                                stroke={TIER_COLORS[tc.to] || "#9ca3af"}
                                strokeDasharray="4 4"
                                label={{ value: `Tier ${tc.to}`, position: "top", fontSize: 10, fill: TIER_COLORS[tc.to] || "#9ca3af" }}
                            />
                        ))}

                        {/* Risk threshold lines */}
                        <ReferenceLine y={75} stroke="#ef4444" strokeDasharray="3 3" strokeOpacity={0.5} />
                        <ReferenceLine y={50} stroke="#f97316" strokeDasharray="3 3" strokeOpacity={0.5} />
                        <ReferenceLine y={25} stroke="#facc15" strokeDasharray="3 3" strokeOpacity={0.5} />

                        {/* Historical area */}
                        <Area
                            type="monotone"
                            dataKey="riskScore"
                            stroke="#3b82f6"
                            fill="url(#scoreGradient)"
                            strokeWidth={2}
                            dot={snapshots.length <= 30}
                            name="Risk Score"
                            connectNulls={false}
                        />

                        {/* Forecast dashed line */}
                        {forecast?.score90Day != null && (
                            <Line
                                type="monotone"
                                dataKey="forecastScore"
                                stroke="#8b5cf6"
                                strokeWidth={2}
                                strokeDasharray="6 3"
                                dot={false}
                                name="Forecast"
                                connectNulls={false}
                                legendType="line"
                            >
                                <LabelList dataKey="horizon" content={HorizonLabel} />
                            </Line>
                        )}
                    </ComposedChart>
                </ResponsiveContainer>

                {forecast?.score90Day != null && (
                    <div className="flex items-center gap-4 mt-2 text-xs text-gray-400">
                        <div className="flex items-center gap-1.5">
                            <div className="w-5 h-0.5 bg-blue-500 rounded"></div>
                            <span>Historical</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                            <div className="w-5 border-t-2 border-dashed border-purple-500"></div>
                            <span>AI Forecast</span>
                        </div>
                        {forecast.confidenceBandLow != null && (
                            <div className="flex items-center gap-1.5">
                                <div className="w-5 h-3 bg-purple-200 rounded-sm opacity-60"></div>
                                <span>90% Confidence Band</span>
                            </div>
                        )}
                    </div>
                )}
            </Card>

            {/* Findings breakdown trend */}
            <Card>
                <h3 className="text-lg font-semibold mb-0.5">Findings Breakdown Over Time</h3>
                <p className="text-xs text-gray-400 mb-4">
                    Historical finding counts per daily snapshot — includes findings that have since been resolved.
                    Past totals may exceed the current active count ({snapshots.length > 0 ? snapshots[snapshots.length - 1].totalFindings : "—"} today).
                </p>
                <ResponsiveContainer width="100%" height={200}>
                    <ComposedChart data={snapshots} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis dataKey="snapshotDate" tickFormatter={formatDate} tick={{ fontSize: 12 }} stroke="#9ca3af" />
                        <YAxis tick={{ fontSize: 12 }} stroke="#9ca3af" />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend />
                        <Area type="monotone" dataKey="criticalCount" stackId="1" stroke="#b91c1c" fill="#fca5a5" name="Critical" />
                        <Area type="monotone" dataKey="highCount"     stackId="1" stroke="#ea580c" fill="#fdba74" name="High" />
                        <Area type="monotone" dataKey="mediumCount"   stackId="1" stroke="#ca8a04" fill="#fde68a" name="Medium" />
                        <Area type="monotone" dataKey="lowCount"      stackId="1" stroke="#16a34a" fill="#bbf7d0" name="Low" />
                    </ComposedChart>
                </ResponsiveContainer>
            </Card>
        </div>
    );
};

export default VendorScoreTrend;
